import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthDialogComponent } from './components/auth-dialog/auth-dialog.component';
import { clearAdReturnFlag } from '@core/constants/ad-return.const';
import { RoutePath } from '@core/enums/route-path.enum';
import { AuthService } from '@api/auth/auth.service';
import { AdAuthDialogComponent } from './components/ad-auth-dialog/ad-auth-dialog.component';
import { TokenService } from '@services/token.service';
import { NotificationService } from '@services/notification.service';
import { TrackerService } from '@services/tracker.service';
import { TrackerCode } from '@models/tracker-event.model';
import { Logger } from '@utils/logger.util';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UsersService } from '@services/users.service';
import { parseAuthExternal } from './helpers/parse-auth-external.helper';

type AuthView = 'initial' | 'sso' | 'external' | 'forgot-password';

@Component({
    selector: 'app-auth',
    templateUrl: './auth.component.html',
    styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit {
    public isNavigating = false;
    public currentView: AuthView = 'initial';
    public authExternalForm: FormGroup;
    public forgotPasswordForm: FormGroup;
    public messageError = '';
    public forgotPasswordMessage = '';
    public isLoading = false;
    public isForgotPasswordLoading = false;
    public hidePassword = true;
    private loginSuccessful = false;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        public dialog: MatDialog,
        private authService: AuthService,
        private tokenService: TokenService,
        private notificationService: NotificationService,
        private trackerService: TrackerService,
        private fb: FormBuilder,
        private usersService: UsersService
    ) {
        this.authExternalForm = this.fb.group({
            space: ['EPAGO', [Validators.required]],
            domain: ['0', [Validators.required]],
            userName: ['', [Validators.required, Validators.minLength(3)]],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });

        this.forgotPasswordForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]]
        });
    }

    ngOnInit(): void {
        const token = this.tokenService.getToken();
        const isTokenValid = token && !this.tokenService.isTokenExpired();

        if (isTokenValid) {
            Logger.debug('Token válido detectado, redirigiendo a backoffice');
            this.router.navigate([`/${RoutePath.BACKOFFICE}/${RoutePath.HOME}`]);
        }
    }

    public selectSSO(): void {
        this.currentView = 'sso';
        this.goToAdLink();
    }

    public selectExternal(): void {
        this.currentView = 'external';
        setTimeout(() => {
            const firstInput = document.querySelector('input[formControlName="userName"]') as HTMLInputElement;
            if (firstInput) {
                firstInput.focus();
            }
        }, 300);
    }

    public goBack(): void {
        if (this.currentView === 'forgot-password') {
            this.currentView = 'external';
            this.forgotPasswordMessage = '';
            this.forgotPasswordForm.reset();
        } else {
            this.currentView = 'initial';
            this.messageError = '';
            this.authExternalForm.reset({
                space: 'EPAGO',
                domain: '0'
            });
        }
    }

    public async goToAdLink() {
        clearAdReturnFlag();
        try {
            const authResponse = await this.authService.adGetLink();
            const url = authResponse.response?.data;

            if (!url) {
                this.notificationService.showError(
                    'No se pudo obtener el enlace de autenticación de Active Directory'
                );

                await this.trackerService.trackAuthEvent(
                    'ERROR',
                    TrackerCode.AUTH_AD_LINK_ERROR,
                    { reason: 'No URL received' }
                );
                this.currentView = 'initial';
                return;
            }

            this.openDialogAd(url);

            await this.trackerService.trackAuthEvent(
                'SUCCESS',
                'AUTH_AD_LINK_OBTAINED',
                { url }
            );
        } catch (error) {
            Logger.error('Error al obtener enlace AD:', error);
            this.notificationService.handleHttpError(
                error,
                'Error al conectar con Active Directory. Por favor, intenta nuevamente.',
                'goToAdLink'
            );

            await this.trackerService.trackAuthEvent(
                'ERROR',
                TrackerCode.AUTH_AD_LINK_ERROR,
                { error }
            );
            this.currentView = 'initial';
        }
    }

    public async externalLogin() {
        if (this.isLoading || this.loginSuccessful) {
            return;
        }

        if (this.authExternalForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        try {
            this.isLoading = true;
            this.messageError = '';

            const authResponse = await this.authService.authExternal(parseAuthExternal(this.authExternalForm.value));

            if (authResponse?.success) {
                this.loginSuccessful = true;
                this.messageError = '';
                this.usersService.saveUser(this.authExternalForm.value.userName);
                this.isNavigating = true;
                this.goToPath(`${RoutePath.BACKOFFICE}/${RoutePath.HOME}`);
            } else {
                this.messageError = 'Usuario o contraseña incorrecto';
                this.isLoading = false;
            }
        } catch (error) {
            this.messageError = 'Error al iniciar sesión. Verifica tus credenciales.';
            console.error('Error en login externo:', error);
            this.isLoading = false;
        }
    }

    private goToPath(path: string) {
        this.router.navigate([path]);
    }

    private markFormGroupTouched() {
        Object.keys(this.authExternalForm.controls).forEach(key => {
            const control = this.authExternalForm.get(key);
            control?.markAsTouched();
        });
    }

    openDialogAd(url: string): void {
        window.location.href = url;
    }

    public onForgotPassword(): void {
        this.currentView = 'forgot-password';
        setTimeout(() => {
            const emailInput = document.querySelector('input[formControlName="email"]') as HTMLInputElement;
            if (emailInput) {
                emailInput.focus();
            }
        }, 300);
    }

    public async submitForgotPassword(): Promise<void> {
        if (this.forgotPasswordForm.invalid) {
            this.markForgotPasswordFormTouched();
            return;
        }

        try {
            this.isForgotPasswordLoading = true;
            this.forgotPasswordMessage = '';

            const forgotPasswordPayload = {
                space: 'EPAGO',
                authentication: {
                    domain: '0',
                    userName: this.forgotPasswordForm.value.email
                }
            };

            const response = await this.authService.forgotPassword(forgotPasswordPayload);

            if (response?.success) {
                this.forgotPasswordMessage = 'Se ha enviado un enlace de recuperación a tu correo electrónico.';
                setTimeout(() => {
                    this.goBack();
                }, 3000);
            } else {
                this.forgotPasswordMessage = 'No se pudo enviar el enlace de recuperación. Verifica tu correo.';
            }
        } catch (error) {
            this.forgotPasswordMessage = 'Error al enviar el enlace de recuperación. Intenta nuevamente.';
            console.error('Error en forgot password:', error);
        } finally {
            this.isForgotPasswordLoading = false;
        }
    }

    private markForgotPasswordFormTouched(): void {
        Object.keys(this.forgotPasswordForm.controls).forEach(key => {
            const control = this.forgotPasswordForm.get(key);
            control?.markAsTouched();
        });
    }

    get userNameControl() {
        return this.authExternalForm.get('userName');
    }

    get passwordControl() {
        return this.authExternalForm.get('password');
    }

    get isFormValid(): boolean {
        return this.authExternalForm.valid && !this.isLoading;
    }

    get emailControl() {
        return this.forgotPasswordForm.get('email');
    }

    get isForgotPasswordFormValid(): boolean {
        return this.forgotPasswordForm.valid && !this.isForgotPasswordLoading;
    }
}
